import * as React from "react";
import Loadable from "react-loadable";
import {
  Route,
  Redirect,
  RouteComponentProps,
  withRouter,
  Switch,
} from "react-router";
import { useSelector } from "react-redux";

import { ROUTES } from "@config/soft.routing";
import { CLIENT_DASHBOARD_TABS } from "@config/softTestIds/clientDashboard";
import { LOADABLE_COMPONENT_DELAY } from "@config/consts";

import { Container } from "@ui/Atoms/Grid";
import { FadeInContainer } from "@ui/Atoms";
import {
  LoadingDashboardSkeleton,
  ProfileDashboardSkeleton,
} from "@ui/Skeletons/Skeletons";
import { HeaderTabsWLinks, ImpersonatorNotification } from "@ui/Molecules";

import { useTranslate } from "@hooks/useTranslate";

import {
  isUserAdmin,
  isAdminImpersonator as isAdminImpersonatorSelector,
} from "@soft/redux/user/user.selectors";
import {
  getUserNotificationValue,
  USER_NOTIFICATIONS,
} from "@soft/redux/userNotifications";
import { IStore } from "@soft/redux/reducers";
import { NotFound } from "@soft/pages/NotFound";

import { MyCleaningsDashboardViewSkeleton } from "./MyCleaningsDashboard/MyCleaningsDashboard.skeleton";

interface Props extends RouteComponentProps {
  currentLanguage: string;
  currentUrl: string;
}

const LoadableMyCleaningsDashboard: any = Loadable({
  loader: () => import("./MyCleaningsDashboard"),
  render(loaded: any, props): JSX.Element {
    const Component = loaded.MyCleaningsDashboard;
    return <Component {...(props as any)} />;
  },
  loading: MyCleaningsDashboardViewSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableBookingsToValidateDashboard: any = Loadable({
  loader: () => import("./BookingsToValidateDashboard"),
  render(loaded: any): JSX.Element {
    const Component = loaded.BookingsToValidateDashboard;
    return <Component />;
  },
  loading: LoadingDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableMyEmployeesDashboard: any = Loadable({
  loader: () => import("./MyEmployeesDashboard"),
  render(loaded: any): JSX.Element {
    const Component = loaded.MyEmployeesDashboard;
    return <Component />;
  },
  loading: LoadingDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableProfileDashboard: any = Loadable({
  loader: () => import("./ProfileDashboard"),
  render(loaded: any): JSX.Element {
    const Component = loaded.ProfileDashboard;
    return <Component />;
  },
  loading: ProfileDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableOptionsDashboard: any = Loadable({
  loader: () => import("./OptionsDashboard"),
  render(loaded: any): JSX.Element {
    const Component = loaded.OptionsDashboard;
    return <Component />;
  },
  loading: LoadingDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const ClientDashboard = withRouter((props: Props) => {
  const translate = useTranslate("batsoft_pwa_navigation");

  const isAdmin = useSelector(isUserAdmin);
  const isAdminImpersonator = useSelector(isAdminImpersonatorSelector);
  const hasBookingsToValidate = useSelector((store: IStore) =>
    getUserNotificationValue(
      store,
      USER_NOTIFICATIONS.HAS_BOOKINGS_TO_VALIDATE,
    ),
  );

  if (
    props.location.pathname === ROUTES.CLIENT_DASHBOARD ||
    props.location.pathname ===
      ROUTES.CLIENT_DASHBOARD + ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS
  ) {
    return (
      <Redirect
        to={ROUTES.CLIENT_DASHBOARD + ROUTES.CLIENT_DASHBOARD_MY_SCHEDULE}
      />
    );
  }

  const tabs = [
    {
      testId: CLIENT_DASHBOARD_TABS.MY_SCHEDULE,
      to: `${props.match.url}${ROUTES.CLIENT_DASHBOARD_MY_SCHEDULE}`,
      label: translate("subnav_my_schedule"),
    },
    ...(hasBookingsToValidate
      ? [
          {
            testId: CLIENT_DASHBOARD_TABS.BOOKINGS_TO_VALIDATE,
            to: `${props.match.url}${ROUTES.CLIENT_DASHBOARD_BOOKINGS_TO_VALIDATE}`,
            label: translate("subnav_bookings_to_validate"),
          },
        ]
      : []),
    {
      testId: CLIENT_DASHBOARD_TABS.MY_EMPLOYEES,
      to: `${props.match.url}${ROUTES.CLIENT_DASHBOARD_MY_EMPLOYEES}`,
      label: translate("subnav_my_employees"),
    },
    {
      testId: CLIENT_DASHBOARD_TABS.PROFILE,
      to: `${props.match.url}${ROUTES.CLIENT_DASHBOARD_PROFILE}`,
      label: translate("subnav_profile"),
    },
    {
      testId: CLIENT_DASHBOARD_TABS.OPTIONS,
      to: `${props.match.url}${ROUTES.CLIENT_DASHBOARD_OPTIONS}`,
      label: translate("subnav_options"),
    },
  ];

  const hideHeaderTabs =
    props.location.pathname.includes(ROUTES.CLIENT_DASHBOARD_SKIP_CLEANING) ||
    props.location.pathname.includes(ROUTES.CLIENT_DASHBOARD_RATING);

  if (isAdmin && !isAdminImpersonator) {
    return <ImpersonatorNotification />;
  }

  return (
    <Container>
      {!hideHeaderTabs && <HeaderTabsWLinks tabs={tabs} />}
      <Switch>
        <Route
          path={`${props.match.url}${ROUTES.CLIENT_DASHBOARD_MY_SCHEDULE}`}
          render={() => (
            <FadeInContainer>
              <LoadableMyCleaningsDashboard />
            </FadeInContainer>
          )}
        />

        <Route
          exact
          path={`${props.match.url}${ROUTES.CLIENT_DASHBOARD_BOOKINGS_TO_VALIDATE}`}
          render={() => (
            <FadeInContainer>
              <LoadableBookingsToValidateDashboard />
            </FadeInContainer>
          )}
        />

        <Route
          path={`${props.match.url}${ROUTES.CLIENT_DASHBOARD_MY_EMPLOYEES}`}
          render={() => (
            <FadeInContainer>
              <LoadableMyEmployeesDashboard />
            </FadeInContainer>
          )}
        />

        <Route
          exact
          path={`${props.match.url}${ROUTES.CLIENT_DASHBOARD_PROFILE}`}
          render={() => (
            <FadeInContainer>
              <LoadableProfileDashboard />
            </FadeInContainer>
          )}
        />

        <Route
          path={`${props.match.url}${ROUTES.CLIENT_DASHBOARD_OPTIONS}`}
          render={() => (
            <FadeInContainer>
              <LoadableOptionsDashboard />
            </FadeInContainer>
          )}
        />

        <Route render={() => <NotFound isNested />} />
      </Switch>
    </Container>
  );
});

export { ClientDashboard };
